import { useState } from 'react';
import './whatsapp-modal.css';
import { X, Plus, Trash2, Send, Calendar, Image, Type, Link, XSquare } from 'lucide-react';
import { toast } from 'react-toastify';

const WhatsAppModal = ({ isOpen, onClose, cardData, reportRange, userCount, customStartDate, customEndDate }) => {
  const [formData, setFormData] = useState({
    templateName: '',
    headerType: 'none',
    headerText: '',
    headerImage: null,
    bodyText: '',
    variables: [],
    buttons: []
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const addVariable = () => {
    if (formData.variables.length >= 10) {
      toast.error('Maximum 10 variables allowed');
      return;
    }

    setFormData(prev => ({
      ...prev,
      variables: [...prev.variables, { id: Date.now(), value: '' }]
    }));
  };

  const removeVariable = (id) => {
    setFormData(prev => ({
      ...prev,
      variables: prev.variables.filter(v => v.id !== id)
    }));
  };

  const updateVariable = (id, value) => {
    setFormData(prev => ({
      ...prev,
      variables: prev.variables.map(v => v.id === id ? { ...v, value } : v)
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        headerImage: file
      }));
    }
  };

  const removeImage = () => {
    setFormData(prev => ({
      ...prev,
      headerImage: null
    }));
  };

  const addButton = () => {
    if (formData.buttons.length >= 3) {
      toast.error('Maximum 3 buttons allowed');
      return;
    }

    setFormData(prev => ({
      ...prev,
      buttons: [...prev.buttons, {
        id: Date.now(),
        type: 'url',
        copyText: ''
      }]
    }));
  };

  const removeButton = (id) => {
    setFormData(prev => ({
      ...prev,
      buttons: prev.buttons.filter(button => button.id !== id)
    }));
  };

  const updateButton = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      buttons: prev.buttons.map(button =>
        button.id === id ? { ...button, [field]: value } : button
      )
    }));
  };

  const handleSend = () => {
    console.log('Sending WhatsApp message:', {
      ...formData,
      cardData,
      reportRange,
      customStartDate,
      customEndDate,
      userCount
    });
    onClose();
  };

  const getReportRangeDisplay = () => {
    const rangeMap = {
      'today': 'Today',
      'yesterday': 'Yesterday',
      '7 days': 'Last 7 Days',
      '30 days': 'Last 30 Days',
      '90 days': 'Last 90 Days',
      'lifetime': 'Lifetime',
      'custom': `Custom (${customStartDate} to ${customEndDate})`
    };
    return rangeMap[reportRange] || reportRange;
  };

  if (!isOpen) return null;

  return (
    <div className="ws-modal-overlay">
      <div className="ws-modal-content">
        <div className="ws-modal-header">
          <div>
            <h2 className="ws-modal-title">
              Send WhatsApp Message to {cardData?.label}
            </h2>
            <div className="ws-info-section">
              <div className="ws-info-box">
                <p>• Use $name in variable for users name</p>
              </div>
              <div className="ws-filter-info-box">
                <Calendar size={14} />
                <span>Filter: {getReportRangeDisplay()}</span>
              </div>
            </div>
          </div>
          <button onClick={onClose} className="ws-close-btn">
            <X size={24} />
          </button>
        </div>

        <div className="ws-form">
          {/* Template Name */}
          <div className="ws-form-group">
            <label>Template Name</label>
            <input
              type="text"
              name="templateName"
              value={formData.templateName}
              onChange={handleInputChange}
              placeholder="Enter template name..."
              className="ws-input"
            />
          </div>

          {/* Header Section */}
          <div className="ws-section-container">
            <h3 className="ws-section-title">Header</h3>

            <div className="ws-radio-group">
              <label className="ws-radio-option">
                <input
                  type="radio"
                  name="headerType"
                  value="none"
                  checked={formData.headerType === 'none'}
                  onChange={handleInputChange}
                />
                <XSquare size={16} />
                <span>None</span>
              </label>

              <label className="ws-radio-option">
                <input
                  type="radio"
                  name="headerType"
                  value="text"
                  checked={formData.headerType === 'text'}
                  onChange={handleInputChange}
                />
                <Type size={16} />
                <span>Text</span>
              </label>

              <label className="ws-radio-option">
                <input
                  type="radio"
                  name="headerType"
                  value="image"
                  checked={formData.headerType === 'image'}
                  onChange={handleInputChange}
                />
                <Image size={16} />
                <span>Image</span>
              </label>
            </div>

            {formData.headerType === 'text' && (
              <div className="ws-form-group">
                <input
                  type="text"
                  name="headerText"
                  value={formData.headerText}
                  onChange={handleInputChange}
                  placeholder="Enter header text..."
                  className="ws-input"
                />
              </div>
            )}

            {formData.headerType === 'image' && (
              <div className="ws-image-upload-section">
                {!formData.headerImage ? (
                  <div className="ws-image-upload-area">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="ws-image-input"
                      id="header-image"
                    />
                    <label htmlFor="header-image" className="ws-image-upload-label">
                      <Image size={24} />
                      <span>Click to upload header image</span>
                    </label>
                  </div>
                ) : (
                  <div className="ws-image-preview">
                    <img
                      src={URL.createObjectURL(formData.headerImage)}
                      alt="Header preview"
                      className="ws-preview-image"
                    />
                    <button
                      onClick={removeImage}
                      className="ws-remove-image-btn"
                    >
                      <Trash2 size={14} />
                      Remove
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Body Section */}
          <div className="ws-section-container">
            <h3 className="ws-section-title">Body</h3>

            <div className="ws-variables-section">
              {formData.variables.map((variable, index) => (
                <div key={variable.id} className="ws-variable-item">
                  <div className="ws-variable-header">
                    <label>Variable {index + 1}</label>
                    <button
                      onClick={() => removeVariable(variable.id)}
                      className="ws-remove-variable-btn"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <input
                    type="text"
                    value={variable.value}
                    onChange={(e) => updateVariable(variable.id, e.target.value)}
                    placeholder="Enter variable value..."
                    className="ws-input"
                  />
                </div>
              ))}

              {formData.variables.length < 10 && (
                <div className="ws-add-variable-container">
                  <button onClick={addVariable} className="ws-add-variable-btn">
                    <Plus size={18} />
                    Add Variable
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Button Section */}
          <div className="ws-section-container">
            <div className="ws-section-header">
              <h3 className="ws-section-title">Buttons</h3>
            </div>

            {formData.buttons.map((button, index) => (
              <div key={button.id} className="ws-button-item">
                <div className="ws-button-header">
                  <label>Button {index + 1}</label>
                  <button
                    onClick={() => removeButton(button.id)}
                    className="ws-remove-button-btn"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>

                <div className="ws-button-type-group">
                  <label className="ws-radio-option">
                    <input
                      type="radio"
                      checked={button.type === 'url'}
                      onChange={(e) => updateButton(button.id, 'type', 'url')}
                    />
                    <Link size={16} />
                    <span>URL</span>
                  </label>

                  <label className="ws-radio-option">
                    <input
                      type="radio"
                      checked={button.type === 'copy'}
                      onChange={(e) => updateButton(button.id, 'type', 'copy')}
                    />
                    <Type size={16} />
                    <span>Copy Text</span>
                  </label>
                </div>

                {button.type === 'copy' && (
                  <div className="ws-form-group">
                    <input
                      type="text"
                      value={button.copyText || ''}
                      onChange={(e) => updateButton(button.id, 'copyText', e.target.value)}
                      placeholder="Text to copy..."
                      className="ws-input"
                    />
                  </div>
                )}
              </div>
            ))}

            {formData.buttons.length < 3 && (
              <div className="ws-add-button-container">
                <button
                  onClick={addButton}
                  className="ws-add-button-btn"
                >
                  <Plus size={18} />
                  Add Button
                </button>
              </div>
            )}
          </div>

          <button
            onClick={handleSend}
            disabled={!formData.templateName}
            className={`ws-send-btn ${!formData.templateName ? 'disabled' : ''}`}
          >
            <Send size={18} />
            Send to {userCount?.toLocaleString() || '0'} Users
          </button>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppModal;